import { Component, OnInit, inject, input, model, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { FinanceApiService, FinancialAccountRecord, FinancialBook } from '../finance/finance-api.service';
import {
  SalesDocumentProfileAdjustmentFactor,
  SalesDocumentProfileFinanceConfig
} from './sales-api.service';

type EnumOption = { value: string; labelKey: string };
type AccountOption = { label: string; value: string };

@Component({
  standalone: true,
  selector: 'ain-sales-document-profile-finance',
  imports: [FormsModule, ButtonModule, InputTextModule, InputNumberModule, SelectModule, TableModule],
  template: `
    <section class="finance-card">
      <h3>{{ t('sales.documentProfiles.finance.title') }}</h3>
      <p class="hint">{{ t('sales.documentProfiles.finance.hint') }}</p>

      <div class="grid">
        <label>
          <span>{{ t('sales.documentProfiles.finance.ledgerBook') }}</span>
          <p-select
            [options]="books()"
            optionValue="id"
            [filter]="true"
            filterBy="code,name"
            [(ngModel)]="finance().ledgerBookId"
            (ngModelChange)="onBookChanged($event)"
            appendTo="body">
            <ng-template pTemplate="selectedItem" let-book>
              {{ book ? (book.code + ' - ' + book.name) : t('sales.documentProfiles.finance.selectBook') }}
            </ng-template>
            <ng-template pTemplate="item" let-book>
              {{ book.code }} - {{ book.name }}
            </ng-template>
          </p-select>
        </label>
      </div>

      <h4>{{ t('sales.documentProfiles.finance.postingAccounts') }}</h4>
      <div class="grid accounts">
        <label>
          <span>{{ t('sales.documentProfiles.finance.receivableAccount') }}</span>
          <p-select [options]="accountOptions()" optionLabel="label" optionValue="value" [(ngModel)]="finance().postingAccounts.receivableAccountId" [disabled]="!finance().ledgerBookId" [filter]="true" filterBy="label" appendTo="body" />
        </label>
        <label>
          <span>{{ t('sales.documentProfiles.finance.revenueAccount') }}</span>
          <p-select [options]="accountOptions()" optionLabel="label" optionValue="value" [(ngModel)]="finance().postingAccounts.revenueAccountId" [disabled]="!finance().ledgerBookId" [filter]="true" filterBy="label" appendTo="body" />
        </label>
        <label>
          <span>{{ t('sales.documentProfiles.finance.inventoryAccount') }}</span>
          <p-select [options]="accountOptions()" optionLabel="label" optionValue="value" [(ngModel)]="finance().postingAccounts.inventoryAccountId" [disabled]="!finance().ledgerBookId" [filter]="true" filterBy="label" appendTo="body" />
        </label>
        <label>
          <span>{{ t('sales.documentProfiles.finance.costOfSalesAccount') }}</span>
          <p-select [options]="accountOptions()" optionLabel="label" optionValue="value" [(ngModel)]="finance().postingAccounts.costOfSalesAccountId" [disabled]="!finance().ledgerBookId" [filter]="true" filterBy="label" appendTo="body" />
        </label>
      </div>

      <div class="factor-header">
        <h4>{{ t('sales.documentProfiles.finance.adjustmentFactors') }}</h4>
        <button pButton type="button" [label]="t('sales.documentProfiles.finance.addFactor')" severity="secondary" (click)="addFactor()" [disabled]="!finance().ledgerBookId"></button>
      </div>

      <p-table [value]="finance().adjustmentFactors">
        <ng-template pTemplate="header">
          <tr>
            <th>{{ t('sales.documentProfiles.finance.factorName') }}</th>
            <th>{{ t('sales.documentProfiles.finance.factorSystemCode') }}</th>
            <th>{{ t('sales.documentProfiles.finance.factorOperation') }}</th>
            <th>{{ t('sales.documentProfiles.finance.factorValueType') }}</th>
            <th>{{ t('sales.documentProfiles.finance.factorValue') }}</th>
            <th>{{ t('sales.documentProfiles.finance.factorAccount') }}</th>
            <th></th>
          </tr>
        </ng-template>
        <ng-template pTemplate="body" let-row let-i="rowIndex">
          <tr>
            <td><input pInputText type="text" [(ngModel)]="row.name" /></td>
            <td>
              <p-select [options]="systemCodes" optionValue="value" [(ngModel)]="row.systemCode" appendTo="body">
                <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : '-' }}</ng-template>
                <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
              </p-select>
            </td>
            <td>
              <p-select [options]="operationTypes" optionValue="value" [(ngModel)]="row.operationType" appendTo="body">
                <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : '-' }}</ng-template>
                <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
              </p-select>
            </td>
            <td>
              <p-select [options]="valueTypes" optionValue="value" [(ngModel)]="row.valueType" appendTo="body">
                <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : '-' }}</ng-template>
                <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
              </p-select>
            </td>
            <td><p-inputNumber [(ngModel)]="row.value" [min]="0" mode="decimal" [minFractionDigits]="0" [maxFractionDigits]="4" /></td>
            <td>
              <p-select [options]="accountOptions()" optionLabel="label" optionValue="value" [(ngModel)]="row.accountId" [disabled]="!finance().ledgerBookId" [filter]="true" filterBy="label" appendTo="body" />
            </td>
            <td><button pButton type="button" icon="pi pi-trash" severity="danger" text="true" (click)="removeFactor(i)"></button></td>
          </tr>
        </ng-template>
      </p-table>
    </section>
  `,
  styles: [`
    .finance-card { border: 1px solid #e5e7eb; border-radius: 12px; padding: 12px; background: #fff; display: grid; gap: 10px; }
    h3, h4 { margin: 0; font-size: 15px; }
    .hint { margin: 0; color: #64748b; font-size: 13px; }
    .grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; }
    .accounts { grid-template-columns: repeat(2, minmax(0, 1fr)); }
    label { display: grid; gap: 6px; }
    .factor-header { display: flex; justify-content: space-between; align-items: center; }
    @media (max-width: 760px) { .grid, .accounts { grid-template-columns: 1fr; } }
  `]
})
export class SalesDocumentProfileFinanceComponent implements OnInit {
  private readonly financeApi = inject(FinanceApiService);
  private readonly localization = inject(LocalizationService);

  readonly finance = model.required<SalesDocumentProfileFinanceConfig>();
  readonly issueFinancialDocument = input(false);

  readonly books = signal<FinancialBook[]>([]);
  readonly accountOptions = signal<AccountOption[]>([]);

  readonly operationTypes: EnumOption[] = [
    { value: 'Reducer', labelKey: 'sales.documentProfiles.finance.operation.Reducer' },
    { value: 'Increaser', labelKey: 'sales.documentProfiles.finance.operation.Increaser' },
    { value: 'Neutral', labelKey: 'sales.documentProfiles.finance.operation.Neutral' }
  ];

  readonly systemCodes: EnumOption[] = [
    { value: 'Tax', labelKey: 'sales.documentProfiles.finance.systemCode.Tax' },
    { value: 'Discount', labelKey: 'sales.documentProfiles.finance.systemCode.Discount' },
    { value: 'DeliveryCost', labelKey: 'sales.documentProfiles.finance.systemCode.DeliveryCost' },
    { value: 'Shipping', labelKey: 'sales.documentProfiles.finance.systemCode.Shipping' },
    { value: 'Custom', labelKey: 'sales.documentProfiles.finance.systemCode.Custom' }
  ];

  readonly valueTypes: EnumOption[] = [
    { value: 'Percent', labelKey: 'sales.documentProfiles.finance.valueType.Percent' },
    { value: 'FixedAmount', labelKey: 'sales.documentProfiles.finance.valueType.FixedAmount' }
  ];

  ngOnInit(): void {
    this.ensureShape();
    this.financeApi.listBooks().subscribe({
      next: books => this.books.set(books.filter(item => item.isActive)),
      error: () => this.books.set([])
    });
    if (this.finance().ledgerBookId) {
      this.loadAccounts(this.finance().ledgerBookId!);
    }
  }

  onBookChanged(bookId: string | null): void {
    this.finance.update(value => ({
      ...value,
      ledgerBookId: bookId || undefined,
      postingAccounts: {
        receivableAccountId: undefined,
        revenueAccountId: undefined,
        inventoryAccountId: undefined,
        costOfSalesAccountId: undefined
      },
      adjustmentFactors: value.adjustmentFactors.map(factor => ({ ...factor, accountId: '' }))
    }));
    if (bookId) {
      this.loadAccounts(bookId);
    } else {
      this.accountOptions.set([]);
    }
  }

  addFactor(): void {
    this.finance.update(value => ({
      ...value,
      adjustmentFactors: [
        ...value.adjustmentFactors,
        {
          key: crypto.randomUUID(),
          name: '',
          operationType: 'Reducer',
          systemCode: 'Custom',
          valueType: 'Percent',
          value: 0,
          accountId: '',
          sortOrder: value.adjustmentFactors.length + 1
        }
      ]
    }));
  }

  removeFactor(index: number): void {
    this.finance.update(value => ({
      ...value,
      adjustmentFactors: value.adjustmentFactors.filter((_, i) => i !== index)
    }));
  }

  private loadAccounts(bookId: string): void {
    this.financeApi.listFinancialAccounts(bookId).subscribe({
      next: accounts => this.accountOptions.set(this.toAccountOptions(accounts)),
      error: () => this.accountOptions.set([])
    });
  }

  private toAccountOptions(accounts: FinancialAccountRecord[]): AccountOption[] {
    return accounts
      .filter(item => item.isActive && item.isPostingAllowed)
      .map(item => ({ value: item.id, label: `${item.code} - ${item.name}` }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }

  private ensureShape(): void {
    const current = this.finance();
    this.finance.set({
      ledgerBookId: current.ledgerBookId,
      postingAccounts: {
        receivableAccountId: current.postingAccounts?.receivableAccountId,
        revenueAccountId: current.postingAccounts?.revenueAccountId,
        inventoryAccountId: current.postingAccounts?.inventoryAccountId,
        costOfSalesAccountId: current.postingAccounts?.costOfSalesAccountId
      },
      adjustmentFactors: current.adjustmentFactors ?? []
    });
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
